import { Net } from './net.js';
import { Voice } from './voice.js';
import { makeT, loadStrings, LANG_NAMES } from './i18n.js';
import { icon } from './icons.js';
import { LANGS, UI_LANGS, FLAG_SVG, esc, skeleton, $, colorFor, timer, fmtMs, toast } from './ui.js';
import { addCard, dueCards, deckCSV, loadDeck } from './deck.js';
import { known as knownWords } from './vocab.js';
import taboo from './modes/taboo.js';
import pictionary from './modes/pictionary.js';
import differences from './modes/differences.js';
import escape from './modes/escape.js';
import truthlie from './modes/truthlie.js';
import telephone from './modes/telephone.js';
import pronunciation from './modes/pronunciation.js';
import call, { renderCall, unmountCall } from './modes/call.js';
import lessons from './modes/lessons.js';
import memory from './modes/memory.js';
import mosaic from './modes/mosaic.js';
import uno from './modes/uno.js';
import stories from './modes/stories.js';
import * as live from './live.js';
import * as talk from './talk.js';
import * as float from './float.js';
import { donateButton } from './donate.js';
import { initSupport, showSupport } from './support.js';
import { startPWA, askToInstall, install, installed } from './pwa.js';
import { start as startSync, uid as progressUID } from './sync.js';
import * as account from './account.js';
import { blockMsg, modSay, reportPlayerMsg, unblockMsg, PLAYER_REASONS } from './moderation.js';
import { translate as aiTranslate } from './ai/translator.js';
import * as team from './team.js';
import * as fun from './fun.js';
import * as me from './me.js';
import * as path from './path.js';
import { MIC_TEXT, micPermission, micReason } from './mic-help.js';
import { harvest } from './harvest.js';
import { endSplash } from './splash.js';
import type { SkeletonRoot } from './ui.js';
import type { NewCard } from './deck.js';
import type { Mode } from './mode-types.js';
import type { Ctx } from '../types/ctx';
import type { Lang, Level, Player, RoomView, ServerMsg, StateMsg, ModeView } from '../types/protocol';
import type { AccountDeps } from '/js/account-react.js';
import type { HomeDeps, Profile } from '/js/home-react.js';
import type { ProfileDeps } from '/js/profile-react.js';
import type { LangPicker, RoomDeps } from '/js/room-react.js';
import type { MicGateModal, OverlayDeps, PodiumRow } from '/js/overlays-react.js';
import type { SidebarDeps } from '/js/sidebar-react.js';
import type { ModalDeps, OwnedModal } from '/js/modals-react.js';
import type { AiDeps } from '/js/ai/panel-react.js';

type AccountMod = typeof import('/js/account-react.js');
type HomeMod = typeof import('/js/home-react.js');
type ProfileMod = typeof import('/js/profile-react.js');
type RoomMod = typeof import('/js/room-react.js');
type OverlaysMod = typeof import('/js/overlays-react.js');
type SidebarMod = typeof import('/js/sidebar-react.js');
type ModalsMod = typeof import('/js/modals-react.js');
type AIMod = typeof import('/js/ai/panel-react.js');

/** The room this browser was last seated in, and the secret that reclaims the seat. */
interface Session {
  code: string;
  pid: string;
  secret: string;
  at: number;
}
/** Every modal: the React ones, and the gate into the call. */
type AppModal = OwnedModal | (MicGateModal & { kind: 'mic-gate' });
/** A word from the deck, as a game is offered it. */
interface WordOffer {
  a: string;
  b: string;
  lang: Lang;
  ui: Lang;
  sub: string;
}
// Older Safari only has the prefixed one.
type OldWindow = Window & { webkitAudioContext?: typeof AudioContext };
/** A [data-act] handler: the element clicked, and the click. */
type Action = (el: HTMLElement, e: MouseEvent) => unknown;
// A form's named controls are properties of it, and win over its own `name`.
type HomeForm = Omit<HTMLFormElement, 'name'> & { name?: HTMLInputElement };

// Whether an untrusted value (storage, a form) is one of a fixed set.
const oneOf = <V extends string>(list: readonly V[], v: unknown): v is V => list.includes(v as V);

const MODES: Record<string, Mode> = { taboo, pictionary, differences, escape, truthlie, telephone, pronunciation, call, lessons, memory, mosaic, uno, stories };
const MODE_ORDER: string[] = ['lessons', 'call', 'memory', 'mosaic', 'uno', 'stories', 'taboo', 'pictionary', 'differences', 'escape', 'truthlie', 'telephone', 'pronunciation'];

// ---------- persistent profile / session ----------

const store = {
  get<V>(k: string, fallback: V): V {
    try {
      return (JSON.parse(localStorage.getItem(k) ?? 'null') as V | null) ?? fallback;
    } catch {
      return fallback;
    }
  },
  set(k: string, v: unknown): void {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  },
};

// A profile is who I am, the language I'm learning and my own language, which
// is also the interface language. Older profiles kept it in `native`.
function loadProfile(): Profile {
  // Whatever this or an older version left in storage; checked field by field.
  const p = store.get<Record<string, unknown> | null>('linguino.profile', {}) || {};
  const browserUi = UI_LANGS.find((l) => (navigator.language || '').toLowerCase().startsWith(l)) || 'en';
  const ui = oneOf(UI_LANGS, p.ui) ? p.ui : oneOf(UI_LANGS, p.native) ? p.native : browserUi;
  const learn = oneOf(LANGS, p.learn) ? p.learn
    : oneOf(LANGS, p.target) ? p.target
      : ui === 'en' ? 'es' : 'en';
  // uiSet marks a language a person chose. Without it `ui` is only the
  // browser's language, which is a guess, and a guess must not quietly decide
  // what their microphone listens for.
  // level is '' until somebody says what it is; the server reads that as A1.
  const level = oneOf(LEVELS, p.level) ? p.level : '';
  return { name: typeof p.name === 'string' ? p.name : '', learn, ui, uiSet: p.uiSet === true, level };
}
const LEVELS: Level[] = ['A1', 'A2', 'B1', 'B2'];
const wire = (p: Profile) => ({ name: account.shownName(p.name), target: p.learn, native: p.ui, ui: p.ui, level: p.level || 'A1' });

// The room I'm in survives closing the tab: not to walk back into by itself,
// but so the profile can offer it back for as long as it is plausibly still
// going on.
const SESSION_TTL = 12 * 3600 * 1000;

interface App {
  profile: Profile;
  session: Session | null;
  state: StateMsg | null;
  offset: number;
  online: boolean;
  sideTab: string;
  modeId: string | null;
  modal: AppModal | null;
  lastChatSeen: number;
  callInvite: { seen: string[]; dismissed: string; shownFor?: string };
  callView: 'full' | 'mini';
  callSince: number;
  chatTr: Record<string, string>;
  homeEdit: boolean;
  profileEdit: boolean;
  pendingMode: string | null;
  pendingLesson: { lang: string; id: string } | null;
  pendingInvite: string;
  joining: boolean;
  homeKnown: boolean;
  welcome: boolean;
  welcomeShown: boolean;
}

// Where my seat is kept. Each tab keeps its own (sessionStorage: it survives a
// reload, and a second tab is a second player). localStorage holds the latest
// one too, only so a tab opened fresh can walk back into the room after the
// browser was closed — and that copy is only borrowed: see `tabs` below.
let borrowedSession = false;
function loadSession(): Session | null {
  const fresh = (s: Session | null) => (s && Date.now() - (s.at || 0) < SESSION_TTL ? s : null);
  let own: Session | null = null;
  try {
    own = fresh(JSON.parse(sessionStorage.getItem('linguino.session') || 'null') as Session | null);
  } catch {}
  if (own) return own;
  const shared = fresh(store.get<Session | null>('linguino.session', null));
  borrowedSession = !!shared;
  return shared;
}

const app: App = {
  profile: loadProfile(),
  session: loadSession(),
  state: null,
  offset: 0,
  online: false,
  sideTab: 'feed',
  modeId: null,
  modal: null, // { kind, ... }
  lastChatSeen: 0,
  callInvite: { seen: [], dismissed: '' },
  callView: 'mini', // my call panel while in voice: 'full' or 'mini'
  callSince: 0, // who is in the call, and whom I said "not now" to
  chatTr: {}, // chat message id -> translation into my native language ('' while running)
  homeEdit: false, // the home's form, opened on purpose by somebody it knows
  profileEdit: false, // the pair of languages, opened for a change on the profile
  pendingMode: null, // a game to start the moment a room of my own exists
  pendingLesson: null, // and the lesson to open in it, when the path asked for one
  pendingInvite: '', // a room somebody was invited to but has not walked into
  joining: false, // a room has been asked for and has not answered yet
  // Decided on arrival and not touched again: somebody halfway through filling
  // the form in for the first time is not somebody the app already knows.
  homeKnown: false,
  welcome: false, // the sheet shown once, after an account claims this device's id
  welcomeShown: false,
};

// ---------- who is allowed in ----------
//
// Playing needs an account. Not because of what it protects — there is nothing
// here worth stealing — but because everything the app keeps is somebody's:
// the deck, the lessons, the streak. Kept under a browser's id they belong to
// a browser, and a phone that clears its storage is a person starting over.
//
// The one exception is a server where signing in is not configured at all. It
// has no way to let anybody in, so the rule is suspended rather than applied:
// otherwise a missing key would not be a misconfiguration, it would be a site
// nobody can open.
//
// Until that server has answered, the rule is assumed to be on. The deployment
// that people actually open is configured, and guessing the other way would
// show a stranger the old form for the length of one request and then take it
// away from under them.
const gated = () => (account.ready() ? account.enabled() : true);

// Who is at the keyboard. A device that remembers an account is treated as
// signed in from the first paint — it is, and the library is on its way to
// confirm it — so nobody with an account is shown the page that sells them one.
const signedIn = () => !!account.user();

// Somebody who may not play: this server asks for an account and there is none.
const locked = () => gated() && !signedIn();

// Both are in the static index.html.
const root = document.getElementById('app')! as SkeletonRoot;
const modalRoot = document.getElementById('modal-root')!;
// The reader's own table comes first: drawing in English and then switching
// would flash. Any other table waits until somebody picks that language.
await loadStrings(app.profile.ui);
let t = makeT(app.profile.ui);

let joinedRoom = false;

const net = new Net(onMessage, (status) => {
  app.online = status === 'online';
  render();
});
// Another tab (or device) took my seat back. The server has already let go of
// this connection; stay out, keep the session so the profile offers the room
// again, and say why, instead of reconnecting and throwing the other one out.
net.onReplaced = () => {
  if (voice.on) voice.disable();
  live.reset();
  app.state = null;
  app.modeId = null;
  app.joining = false;
  joinedRoom = false;
  micAsked = false;
  feedSeen = null;
  run = null;
  history.replaceState(null, '', '/');
  render();
  toast(t('error.replaced'), 'bad');
};
const voice = new Voice(net);
// A peer's picture arriving, or the browser's own "Stop sharing" bar.
voice.onChange = () => app.state && render();
// The "support us" penguin never interrupts a room or a call.
initSupport({ t: () => t, busy: () => !!app.state || voice.on });

// ---------- theme (day / night) ----------

// Night unless somebody picked the day (Aurora, like the phone app): the
// operating system's preference is no longer followed.
function currentTheme() {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

function applyThemeColor() {
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', currentTheme() === 'light' ? '#eef6fd' : '#081633');
}
applyThemeColor();

const themeButton = () => {
  const label = currentTheme() === 'light' ? t('theme.dark') : t('theme.light');
  return `<button class="btn small ghost theme-toggle" data-act="theme" title="${label}" aria-label="${label}"><span class="i-wrap">${icon(currentTheme() === 'light' ? 'moon' : 'sun')}</span><span class="tb-label">${label}</span></button>`;
};

// A row of the languages as flags. `exclude` greys out one choice; the forms
// no longer use it, since picking the language I speak swaps the two instead.
const langPicker: LangPicker = (selected, name, change = '', exclude = null) => `<div class="seg learn-seg" role="radiogroup">${LANGS.map((l) => `
  <label class="seg-opt ${l === selected ? 'on' : ''} ${l === exclude ? 'off' : ''}"><input type="radio" name="${name}" value="${l}" ${l === selected ? 'checked' : ''} ${l === exclude ? 'disabled' : ''} ${change ? `data-change="${change}"` : ''}>${FLAG_SVG[l]}<span>${esc(LANG_NAMES[app.profile.ui][l])}</span></label>`).join('')}</div>`;

// Learning the language I speak swaps the two, and the interface follows into
// the language I spoke before.
function setLearn(l: string | undefined): boolean {
  if (!oneOf(LANGS, l)) return false;
  const form = root.querySelector<HTMLFormElement>('form[data-form="home"]') as HomeForm | null;
  if (form?.name) app.profile.name = form.name.value;
  const was = app.state ? ctx().me?.target || app.profile.learn : app.profile.learn;
  app.profile = { ...app.profile, learn: l };
  store.set('linguino.profile', app.profile);
  if (l === app.profile.ui) return setSpeak(was !== l ? was : l === 'en' ? 'es' : 'en');
  if (app.state && !app.state.mode) net.send({ t: 'profile', target: l });
  return true;
}

// Choosing the language I speak also switches the interface to it, and moves
// what I'm learning off it if they clash.
function setSpeak(ui: string | undefined): boolean {
  if (!oneOf(UI_LANGS, ui)) return false;
  const form = root.querySelector<HTMLFormElement>('form[data-form="home"]') as HomeForm | null;
  if (form?.name) app.profile.name = form.name.value;
  const cur = app.state ? ctx().me?.target || app.profile.learn : app.profile.learn;
  // Speaking what I was learning swaps the two: I now learn what I spoke.
  const before = app.profile.ui;
  const learn = cur === ui ? (before !== ui ? before : ui === 'en' ? 'es' : 'en') : cur;
  app.profile = { ...app.profile, ui, learn, uiSet: true };
  t = makeT(ui);
  // Until its table arrives the page reads in English; then it redraws.
  void loadStrings(ui).then(() => render());
  store.set('linguino.profile', app.profile);
  document.documentElement.lang = ui;
  if (app.state) net.send({ t: 'profile', ui, native: ui, target: app.state.mode ? undefined : learn });
  return true;
}

const CREDIT = () => `<footer class="credit">${t('credit.by')} <b>Dio Kotov</b><div class="credit-data"><a href="https://tatoeba.org" target="_blank" rel="noopener">${t('credit.tatoeba')}</a></div></footer>`;

function saveSession(s: Omit<Session, 'at'> | null): void {
  app.session = s ? { ...s, at: Date.now() } : null;
  borrowedSession = false;
  try {
    if (s) {
      sessionStorage.setItem('linguino.session', JSON.stringify(app.session));
      localStorage.setItem('linguino.session', JSON.stringify(app.session));
    } else {
      sessionStorage.removeItem('linguino.session');
      localStorage.removeItem('linguino.session');
    }
  } catch {}
}

// Connect and say hello; after the first 'joined' every reconnect re-attaches.
//
// `joining` is set here and cleared when the room answers. It is not the same
// question as "is there a saved session": since opening Linguino lands on the
// profile, a room can sit in storage for hours with nothing connecting to it,
// and a page that reads that as "reconnecting…" would say so forever.
//
// The server asks for the account's token before it hands out a new seat (it
// does not trust the page to have asked), so every hello carries a fresh one.
// Taking my own seat back needs only its secret, but the token rides along
// anyway: if the seat is gone, the same hello becomes an ordinary join.
function connectWith(hello: Record<string, unknown> | null): void {
  app.joining = true;
  net.connect(async () => {
    const base = app.session ? { t: 'join', code: app.session.code, pid: app.session.pid, secret: app.session.secret } : hello;
    if (!base) return base;
    // The progress id this seat earns for; the server checks it is mine.
    const msg = { ...base, uid: progressUID() };
    const token = await account.token();
    return token ? { ...msg, token } : msg;
  });
}

// ---------- context handed to mode renderers ----------

// Only called while in a room: app.state is there.
function ctx(): Ctx {
  const s = app.state!;
  const room = s.room;
  // The server always lists whoever it is talking to.
  const me = room.players.find((p) => p.id === s.you) as Player;
  return {
    t,
    room,
    // null in the lobby. Only a game's own screens read it, and they are only
    // drawn while that game is running.
    mode: s.mode as ModeView,
    me,
    you: s.you,
    // Steering the current game: the host, or whoever started it.
    isHost: room.hostId === s.you || (!!room.modeBy && room.modeBy === s.you),
    now: () => Date.now() + app.offset,
    player: (id) => room.players.find((p) => p.id === id),
    name: (id) => esc(room.players.find((p) => p.id === id)?.name ?? '?'),
    chip: (id) => playerChip(room, id),
    ui: app.profile.ui,
    lang: (l) => `<span class="pill ${l}">${FLAG_SVG[l as Lang] || ''} ${esc(LANG_NAMES[app.profile.ui]?.[l as Lang] || l)}</span>`,
    langName: (l) => LANG_NAMES[app.profile.ui]?.[l as Lang] || l,
    act: (a, extra = {}) => net.send({ t: 'act', a, ...extra }),
    send: (msg) => net.send(msg),
    openCorrection: (prefill) => openModal({ kind: 'correction', ...(prefill as Omit<OwnedModal & { kind: 'correction' }, 'kind'>) }),
    voice,
    toggleVoice: () => globalActions.voice(),
    toggleMute: () => globalActions.mute(),
    toast,
    // Ctx hands cards over loosely typed; they are the deck's own shape.
    addCard: (card) => addCard({ ...card, id: `call-${Date.now().toString(36)}` }),
    rerender: render,
  };
}

function playerChip(room: RoomView, id: string): string {
  const p = room.players.find((x) => x.id === id);
  if (!p) return '?';
  return `<b style="color:${colorFor(id)}">${esc(p.name)}</b>`;
}

// ---------- inbound ----------

function onMessage(m: ServerMsg): void {
  switch (m.t) {
    case 'joined':
      saveSession({ code: m.code, pid: m.pid, secret: m.secret });
      app.pendingInvite = '';
      history.replaceState(null, '', `/?room=${m.code}`);
      // The server forgets voice state on disconnect; tell it again after a reconnect.
      if (voice.on) voice.announce();
      break;
    case 'state': {
      const s = m;
      app.offset = s.now - Date.now();
      app.state = s;
      app.joining = false;
      team.track(s.room);
      celebrate(s.room, s.you);
      podium(s);
      // A game that beat you leaves the word behind, in the deck.
      if (joinedRoom) harvest(ctx());
      voice.sync(s.room, s.you);
      // Somebody pressed "Lessons" on their profile and this is the room that
      // was opened for it. Only the person who opened it may start it, and only
      // while nothing else is running.
      if (app.pendingMode && !s.mode && s.room.hostId === s.you
          && (s.room.players?.length || 1) >= (MODES[app.pendingMode]?.minPlayers || 2)) {
        const mode = app.pendingMode;
        app.pendingMode = null;
        net.send({ t: 'startMode', mode });
      }
      // The path opened a lesson: once the lessons are on, pick it.
      if (app.pendingLesson && s.mode?.id === 'lessons' && s.mode.phase === 'pick' && s.room.hostId === s.you) {
        const { id, lang } = app.pendingLesson;
        app.pendingLesson = null;
        net.send({ t: 'act', a: 'pick', id, lang });
      }
      if (!joinedRoom) {
        joinedRoom = true;
        offerWords();
        scrollTo(0, 0); // the room starts at the top, not where the home page was scrolled
        live.catchUp(ctx());
        micGate();
      } else if (!micAsked && (s.room.players?.length || 1) > 1) {
        // Somebody has arrived in a room that was empty when it opened.
        micGate();
      }
      render();
      break;
    }
    case 'error':
      // Every refusal that leaves no room to be in: back out, or the socket
      // reconnects and asks the same thing again every second and a half.
      if (['room_not_found', 'room_full', 'sign_in', 'slow_down', 'busy'].includes(m.code)) {
        saveSession(null);
        net.close();
        app.state = null;
        app.joining = false;
        app.pendingMode = null;
        app.pendingLesson = null;
        app.pendingInvite = '';
        history.replaceState(null, '', '/');
        render();
      }
      toast(t(`error.${m.code}`), 'bad');
      break;
    case 'deckAdd':
      if (addCard(m.card)) toast(t('deck.added'), 'ok');
      render();
      break;
    case 'rtc':
      voice.onSignal(m.from, m.data);
      break;
    case 'speechInterim':
      // Someone's words while they are still speaking.
      if (app.state) live.onInterim(m, ctx());
      break;
    case 'reported': {
      // The server kept a report (or a block, which files one).
      const name = app.state?.room.players.find((p) => p.id === m.pid)?.name || '';
      if (!m.ok) toast(modSay('failed', app.profile.ui), 'bad');
      else toast(modSay(m.block ? 'blocked' : 'reported', app.profile.ui, { name }), 'ok');
      break;
    }
    case 'draw':
    case 'clip':
      if (app.state?.mode && MODES[app.state.mode.id]?.onMessage) MODES[app.state.mode.id].onMessage!(m, ctx());
      break;
  }
}

// ---------- what this browser brings to a game ----------

// The deck, straightened out for the room.
//
// A card stores its two sides in whichever order it was made in: a word saved
// out of a conversation has the foreign word on the front, one saved from the
// vocabulary has it on the back. A game cannot be asked to know that, so it is
// sorted out here — `a` is always the word in the language being learned, `b`
// always what it means — and the server is handed something it can build a
// board from without guessing.
//
// Only what a game could use: a correction is a sentence being fixed, not a
// word with a meaning, so it stays out.
function myWords(): WordOffer[] {
  const out: WordOffer[] = [];
  for (const c of loadDeck()) {
    if (!c || !c.lang || c.source === 'correction') continue;
    // The vocabulary keeps its transliteration in brackets after the word.
    const strip = (t: string) => String(t || '').replace(/\s*\(([^)]*)\)\s*$/, '').trim();
    const bracket = /\(([^)]*)\)\s*$/.exec(c.source === 'call' ? c.front : c.back);
    const foreign = c.source === 'call' ? c.front : c.back;
    const mine = c.source === 'call' ? c.back : c.front;
    const a = strip(foreign);
    const b = strip(mine);
    if (!a || !b) continue;
    out.push({ a, b, lang: c.lang, ui: app.profile.ui, sub: bracket ? bracket[1] : '' });
  }
  return out;
}

// Offered on the way into a room and whenever the deck changes, because a word
// saved during a call should be playable in the next game of the same call.
function offerWords() {
  if (!app.state) return;
  net.send({ t: 'myWords', words: myWords() });
}
addEventListener('linguino:card-added', () => offerWords());
addEventListener('linguino:synced', () => offerWords());

// ---------- rendering ----------

function render(): void {
  // Three screens where there used to be two: the page a stranger sees, the
  // profile that is home for everybody with an account, and a room. Whoever is
  // not drawing has to let go of #app first — React will not share it.
  //
  // Without an account the home page is what it always was, form and all: on a
  // server where signing in is not set up that form is the only way in, and it
  // still works exactly as it did.
  if (!app.state && !signedIn()) {
    profileMod?.unmountProfile();
    renderHome();
  } else if (!app.state) {
    homeMod?.unmountHome();
    renderProfile();
  } else {
    homeMod?.unmountHome();
    profileMod?.unmountProfile();
    renderRoom();
  }
  renderAI(!!app.state);
  renderAccount();
  renderLive();
  renderCallInvite();
  renderCallPanel();
  voiceChimes();
  screenNotices();
  syncHistory();
  renderModal();
}

// ---------- the account, where there is one ----------
//
// Both the room's top bar and the home page leave an empty element for this,
// and it is drawn into that in its own root: the same button in two pages that
// are otherwise two different bundles. While the server says signing in is not
// set up, nothing here draws anything at all.
let accountMod: AccountMod | null = null;
let accountLoading: Promise<void> | null = null;
const accountDeps: AccountDeps = {
  t: (...a) => t(...a),
  cards: () => loadDeck().length,
  welcome: () => app.welcome,
};

function withAccount(then: (m: AccountMod) => void): void {
  if (accountMod) return then(accountMod);
  if (accountLoading || !account.enabled()) return;
  accountLoading = import('/js/account-react.js').then((m) => {
    accountMod = m;
    m.mountAccount(accountDeps);
    render();
  }).catch(() => { accountLoading = null; });
}

function renderAccount() {
  if (!account.enabled()) return;
  const slot = root.querySelector('.acc-mount');
  const offer = document.querySelector('.acc-mount-offer');
  // The welcome has its own root and must not wait for a slot: right after
  // signing in, the profile that holds the button is still being drawn.
  if (!slot && !offer && !app.welcome) return;
  withAccount((m) => {
    if (slot) m.renderAccountButton(slot, account.snapshot());
    if (offer) m.renderAccountOffer(offer, account.snapshot());
    m.renderAccountWelcome(welcomeRoot, account.snapshot());
  });
}

// Drawn beside the modals, so it can sit over whatever is on screen.
const welcomeRoot = (() => {
  const el = document.createElement('div');
  el.id = 'account-welcome';
  document.body.appendChild(el);
  return el;
})();

// Captions ride above everything once somebody is in the voice chat: the
// games, and the translated call too.
function renderLive() {
  const el = document.getElementById('live-root')!;
  if (!app.state) {
    el.hidden = true;
    live.renderBar(el, null, { hidden: true });
    float.renderFloat(null);
    return;
  }
  const c = ctx();
  live.tick(c);
  float.renderFloat(c);
  // Listening counts too: I may have my microphone off and still want to read
  // what the others are saying.
  const anyoneTalking = voice.on || c.room.players.some((p) => p.voice && p.id !== c.you);
  live.renderBar(el, c, { hidden: !anyoneTalking });
}

// The home page is React (web/home). It is the first thing anybody sees, so
// nothing waits for a room: the bundle is asked for as the page boots.
let homeMod: HomeMod | null = null;
let homeLoading: Promise<void> | null = null;
const homeDeps: HomeDeps = {
  t: (...a) => t(...a),
  profile: () => app.profile,
  connecting: () => app.joining && !app.online,
  langPicker: (...a) => langPicker(...a),
  themeButton: () => themeButton(),
  account: () => ({ name: account.user()?.name || '', enabled: account.enabled() }),
  known: () => app.homeKnown || !!account.user()?.name,
  langName: (l) => LANG_NAMES[app.profile.ui]?.[l as Lang] || l,
  editing: () => app.homeEdit,
  gated: () => gated(),
  busy: () => account.snapshot().busy,
  rerender: () => render(),
};

function withHome(then: (m: HomeMod) => void): void {
  if (homeMod) return then(homeMod);
  if (homeLoading) return;
  homeLoading = import('/js/home-react.js').then((m) => {
    homeMod = m;
    m.mountHome(homeDeps);
    if (!app.state) render();
  }).catch(() => { homeLoading = null; });
}

function renderHome() {
  // React owns #app here. The room's skeleton is built by key, and that key
  // must not still say "room" when it comes back, or it would not be rebuilt.
  root._key = 'home';
  withHome((m) => m.renderHome(root));
}

// The profile: home for anybody with an account. Same shape as the home page
// above — one bundle, asked for the first time it is needed, and nothing else
// waits for it.
let profileMod: ProfileMod | null = null;
let profileLoading: Promise<void> | null = null;
const profileDeps: ProfileDeps = {
  t: (...a) => t(...a),
  profile: () => app.profile,
  account: () => ({ name: account.user()?.name || '', email: account.user()?.email || '' }),
  langPicker: (...a) => langPicker(...a),
  themeButton: () => themeButton(),
  langName: (l) => LANG_NAMES[app.profile.ui]?.[l as Lang] || l,
  levelInfo: () => fun.levelInfo(),
  missions: () => fun.missions(),
  missionIcon: (id) => MISSION_ICON[id] || 'target',
  modes: MODES,
  modeOrder: MODE_ORDER,
  streak: () => team.streak(),
  due: () => dueCards().length,
  deckSize: () => loadDeck().length,
  knownWords: () => Object.keys(knownWords()).length,
  lessonsDone: () => lessonCounts().done,
  lessonsOpen: () => lessonCounts().open,
  invite: () => app.pendingInvite,
  session: () => app.session,
  connecting: () => app.joining && !app.online,
  editing: () => app.profileEdit,
  rerender: () => render(),
};

// Lessons keep their own progress, per track: how many times a lesson has been
// run through and the best score it ended on. How many lessons exist is the
// server's business and changes; what can be counted here without asking
// anybody is which ones this person has been through and which ones they have
// been through without getting them right yet.
function lessonCounts(): { done: number; open: number } {
  let done = 0;
  let open = 0;
  for (const v of Object.values(me.lessons())) {
    if (v.best >= 80) done++;
    else if (v.done > 0) open++;
  }
  return { done, open };
}

function withProfile(then: (m: ProfileMod) => void): void {
  if (profileMod) return then(profileMod);
  if (profileLoading) return;
  profileLoading = import('/js/profile-react.js').then((m) => {
    profileMod = m;
    m.mountProfile(profileDeps);
    if (!app.state) render();
  }).catch(() => { profileLoading = null; });
}

function renderProfile() {
  root._key = 'profile';
  withProfile((m) => {
    m.renderProfile(root);
    // React draws the profile after this returns, and the account button goes
    // in a slot inside it: fill the slot once it exists.
    let frames = 30;
    const fill = () => {
      if (root._key !== 'profile') return;
      if (root.querySelector('.acc-mount')) renderAccount();
      else if (frames-- > 0) requestAnimationFrame(fill);
    };
    if (!root.querySelector('.acc-mount')) requestAnimationFrame(fill);
  });
  readyTranslator();
}

// Build the translation pipeline while somebody is reading their own profile,
// so the first subtitle in the room does not have to wait for it.
//
// Only when the weights are already on this device. A pair that is not here yet
// is left alone: starting a hundred-megabyte download because somebody opened
// their profile is not a thing to do to anyone, least of all on a phone.
let warmed = '';
function readyTranslator() {
  const pair = `${app.profile.learn}-${app.profile.ui}`;
  if (warmed === pair || app.profile.learn === app.profile.ui) return;
  warmed = pair;
  import('./ai/translator.js')
    .then((m) => m.warmIfCached(app.profile.learn, app.profile.ui))
    .catch(() => { warmed = ''; });
}

function renderRoom(): void {
  const c = ctx();
  skeleton(root, `room:${app.profile.ui}`, `
    <div class="topbar" id="topbar"></div>
    <div class="layout">
      <main class="stage">
        <div class="card mode-bar" id="mode-bar"></div>
        <div id="mode-tip"></div>
        <div id="stage-body"></div>
      </main>
      <aside class="sidebar" id="sidebar"></aside>
    </div>
    ${CREDIT()}`);

  // My own call view takes the stage for me only; the room keeps playing.
  const myCall = voice.on && app.callView === 'full';
  const mode = app.state!.mode;
  const def = mode ? MODES[mode.id] : null;

  // stage (the skeleton above always has it)
  const body = $(root, '#stage-body')!;
  const modeId = myCall ? 'my-call' : mode?.id || null;
  if (modeId !== app.modeId) {
    if (app.modeId === 'my-call') unmountCall();
    else if (app.modeId && MODES[app.modeId].unmount) MODES[app.modeId].unmount!();
    else if (app.modeId === null && roomMod) roomMod.unmountLobby();
    // Whoever had the stage has handed it back by now; what is left of theirs
    // goes, so the next screen starts on an empty one.
    body.innerHTML = '';
    app.modeId = modeId;
  }
  // What the stage holds, for the room's styles (aurora/room.css): the lobby
  // leads with a big title instead of a bar, a game or the call with a bar.
  const layout = $(root, '.layout');
  if (layout) layout.dataset.stage = myCall ? 'call' : mode ? 'game' : 'lobby';
  if (myCall) renderCall(body, c);
  else if (mode) def!.render(body, c);
  else renderRoomLobby(body, c);

  renderTopAndBar(c);
  renderSidebar(c);
}

// The bar at the top, the strip that says what is being played, and the lobby.
// The bundle arrives the first time somebody is in a room, like the rest.
let roomMod: RoomMod | null = null;
let roomLoading: Promise<void> | null = null;
const roomDeps: RoomDeps = {
  t: (...a) => t(...a),
  voice,
  ui: () => app.profile.ui,
  online: () => app.online,
  dueCount: () => dueCards().length,
  callers: () => callers(),
  callSince: () => app.callSince,
  callClock: () => fmtClock(Date.now() - app.callSince),
  donateButton: () => donateButton(t),
  themeButton: () => themeButton(),
  howButton: (id) => howButton(id),
  langPicker: (...a) => langPicker(...a),
  timerHTML: (deadline) => timer(deadline),
  modeDef: (id) => MODES[id],
  pendingMode: () => app.pendingMode,
  // Only asked of a game that has a status().
  modeStatus: (c) => MODES[c.mode.id].status!(c),
  modes: MODES,
  modeOrder: MODE_ORDER,
  get myCall() { return voice.on && app.callView === 'full'; },
};

function withRoomModule(then: (m: RoomMod) => void): void {
  if (roomMod) return then(roomMod);
  if (roomLoading) return;
  roomLoading = import('/js/room-react.js').then((m) => {
    roomMod = m;
    m.mountRoom(roomDeps);
    if (app.state) render();
  }).catch(() => { roomLoading = null; });
}

function renderTopAndBar(c: Ctx): void {
  withRoomModule((m) => {
    // Both are in the room's skeleton.
    m.renderTopBar($(root, '#topbar')!, c);
    m.renderModeBar($(root, '#mode-bar')!, c);
    const tip = $(root, '#mode-tip');
    if (tip) m.renderModeTip(tip, c);
  });
}

function renderRoomLobby(body: HTMLElement, c: Ctx): void {
  withRoomModule((m) => {
    // A child of the stage, so a game taking over never empties a node React
    // still holds.
    let el = body.querySelector('#lobby-root');
    if (!el) {
      body.innerHTML = '';
      el = document.createElement('div');
      el.id = 'lobby-root';
      body.appendChild(el);
    }
    m.renderLobby(el, c);
  });
}

// The things that float above the room: the invitation into the call, the
// small call panel, the podium, the hint at the browser's microphone question
// and the gate on the way in. One bundle, fetched beside the room's own, so
// the gate is never later than the room it belongs to.
let overlaysMod: OverlaysMod | null = null;
let overlaysLoading: Promise<void> | null = null;
const overlayDeps: OverlayDeps = {
  t: (...a) => t(...a),
  ui: () => app.profile.ui,
  uiSet: () => !!app.profile.uiSet,
  callers: () => callers(),
  voice,
  callSince: () => app.callSince,
  clock: () => fmtClock(Date.now() - app.callSince),
};

function withOverlays(then: (m: OverlaysMod) => void): void {
  if (overlaysMod) return then(overlaysMod);
  if (overlaysLoading) return;
  overlaysLoading = import('/js/overlays-react.js').then((m) => {
    overlaysMod = m;
    m.mountOverlays(overlayDeps);
    if (app.state) render();
  }).catch(() => { overlaysLoading = null; });
}

// The column beside the game is React. Like everything else it has taken over,
// the bundle arrives the first time somebody is in a room.
let sidebarMod: SidebarMod | null = null;
let sidebarLoading: Promise<void> | null = null;
const sidebarDeps: SidebarDeps = {
  t: (...a) => t(...a),
  voice,
  team,
  fun,
  isTalking: (id) => live.isTalking(id),
  talkLine: (c) => talk.talkLineHTML(c),
  missionIcon: (id) => MISSION_ICON[id] || 'target',
  callSince: () => app.callSince,
  get sideTab() { return app.sideTab; },
  lastChatSeen: () => app.lastChatSeen,
  chatTr: () => app.chatTr,
};

function renderSidebar(c: Ctx): void {
  // Reading the chat marks it read, which app.js has always owned.
  if (app.sideTab === 'chat') app.lastChatSeen = c.room.chat.at(-1)?.t || 0;
  const el = $(root, '#sidebar');
  if (!el) return;
  // The element is handed over every time: a change of interface language
  // rebuilds the room and with it this node.
  if (sidebarMod) return sidebarMod.renderSidebar(c, el);
  if (sidebarLoading) return;
  sidebarLoading = import('/js/sidebar-react.js').then((m) => {
    sidebarMod = m;
    m.mountSidebar(el, sidebarDeps);
    m.renderSidebar(ctx());
  }).catch(() => { sidebarLoading = null; });
}


const MISSION_ICON: Record<string, string> = { memory: 'layout-grid', taboo: 'ban', review: 'layers', talk: 'mic', lesson: 'graduation-cap', sentences: 'book-open', save: 'bookmark-plus', win: 'trophy', pictionary: 'palette' };

// ---------- points become XP, and each game ends on a podium ----------

// What a room event counts for in the missions.
const REASON_EVENT: Record<string, string> = {
  'memory.pair': 'memory.pair', 'memory.streak': 'memory.pair',
  'taboo.described': 'taboo.word', 'taboo.guessed': 'taboo.word',
  'lessons.exercise': 'lesson.exercise', 'lessons.accepted': 'lesson.exercise',
  'pictionary.exact': 'pictionary.guess', 'pictionary.close': 'pictionary.guess', 'pictionary.accepted': 'pictionary.guess',
};

let feedSeen: Set<string> | null = null; // feed ids already handled (the backlog on joining is skipped)
let speechSeen: Set<string> | null = null;
function celebrate(room: RoomView, you: string): void {
  if (feedSeen === null) {
    feedSeen = new Set(room.feed.map((f) => f.id));
    speechSeen = new Set(room.speech.map((l) => l.id));
    return;
  }
  for (const f of room.feed) {
    if (feedSeen.has(f.id)) continue;
    feedSeen.add(f.id);
    if (f.kind !== 'score' || f.pid !== you) continue;
    // A score entry says how many points, and what for.
    const pts = f.pts as number;
    const reason = f.reason as string;
    if (pts > 0) {
      if (reason === 'memory.streak') fun.sfx.combo(3);
      else fun.sfx.correct();
      fun.addXP(pts * 5);
    } else fun.sfx.wrong();
    if (REASON_EVENT[reason]) fun.track(REASON_EVENT[reason]);
  }
  for (const l of room.speech) {
    if (speechSeen!.has(l.id)) continue;
    speechSeen!.add(l.id);
    if (l.pid === you) fun.track('call.line');
  }
}

// Scores at the start of a game, to show who did best in it when it ends.
let run: { id: string; key: string; shown: boolean; scores: Record<string, number> } | null = null;
function podium(state: StateMsg): void {
  const mode = state.mode;
  if (!mode) {
    run = null;
    return;
  }
  if (!run || run.id !== mode.id || run.key !== state.room.modeBy + mode.id) {
    run = { id: mode.id, key: state.room.modeBy + mode.id, shown: false, scores: Object.fromEntries(state.room.players.map((p) => [p.id, p.score])) };
  }
  const ended = mode.phase === 'end' || mode.phase === 'done';
  if (!ended || run.shown) return;
  run.shown = true;
  const scores = run.scores;
  if (mode.id === 'lessons') {
    fun.sfx.win();
    fun.confetti();
    return;
  }
  const rows: PodiumRow[] = state.room.players.filter((p) => p.connected || p.score !== (scores[p.id] ?? 0))
    .map((p) => ({ p, pts: p.score - (scores[p.id] ?? p.score) }))
    .sort((a, b) => b.pts - a.pts);
  const winner = rows[0];
  const iWon = winner && winner.p.id === state.you && winner.pts > 0;
  fun.sfx.win();
  fun.confetti();
  fun.addXP(iWon ? 30 : 15);
  if (iWon) fun.track('game.win');
  const order = [rows[1], rows[0], rows[2]].filter(Boolean);
  overlaysMod?.unmountPodium();
  document.getElementById('podium')?.remove();
  const el = document.createElement('div');
  el.id = 'podium';
  document.body.appendChild(el);
  withOverlays((m) => m.renderPodium(el, { order, rows, iWon }));
}


// Discord-style chimes when someone joins or leaves the voice channel I'm in.
let voiceMembers: Set<string> | null = null;
function voiceChimes(): void {
  if (!app.state) {
    voiceMembers = null;
    return;
  }
  const you = app.state.you;
  const now = new Set(app.state.room.players.filter((p) => p.connected && (p.id === you ? voice.on : p.voice)).map((p) => p.id));
  if (voiceMembers && voice.on) {
    const was = voiceMembers;
    const joined = [...now].some((id) => !was.has(id));
    const left = [...voiceMembers].some((id) => !now.has(id));
    if (joined) chime([523, 784]);
    else if (left) chime([784, 523]);
  } else if (voiceMembers && !voice.on && voiceMembers.has(app.state.you)) chime([784, 523]);
  voiceMembers = now;
}

// Somebody starting to share their screen says so once, with a way to see it.
let sharers: Set<string> | null = null;
function screenNotices(): void {
  if (!app.state) {
    sharers = null;
    return;
  }
  const s = app.state;
  const now = new Set(s.room.players.filter((p) => p.connected && p.screen && p.id !== s.you).map((p) => p.id));
  if (sharers) {
    for (const id of now) {
      if (sharers.has(id)) continue;
      const name = s.room.players.find((p) => p.id === id)?.name || '?';
      toast(`${t('screen.started', { name })}${voice.on ? '' : ` · ${t('screen.joinToSee')}`}`, 'ok');
      chime([659, 880]);
    }
  }
  sharers = now;
}

function chime(notes: number[]): void {
  try {
    const ac = (audioCtx ||= new (window.AudioContext || (window as OldWindow).webkitAudioContext)());
    ac.resume?.();
    const t0 = ac.currentTime;
    notes.forEach((f, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      const at = t0 + i * 0.11;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.12, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
      o.connect(g).connect(ac.destination);
      o.start(at);
      o.stop(at + 0.2);
    });
  } catch {}
}



// ---------- joining the voice call, and help with the microphone ----------

// Asks for the microphone with a heads-up first, and explains what to do when
// the browser, the system or another app says no.
async function joinVoice({ listenOnly = false }: { listenOnly?: boolean } = {}): Promise<void> {
  if (!listenOnly) {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return openModal({ kind: 'mic', reason: 'insecure' });
    const state = await micPermission();
    if (state === 'denied') return openModal({ kind: 'mic', reason: 'blocked' });
    if (state !== 'granted') showMicHint(true);
  }
  try {
    await voice.enable({ listenOnly });
    app.callSince = Date.now();
    if (app.callView !== 'full') app.callView = 'mini';
  } catch (e) {
    openModal({ kind: 'mic', reason: micReason(e) });
  } finally {
    showMicHint(false);
    render();
  }
}

function showMicHint(on: boolean): void {
  let el = document.getElementById('mic-hint');
  if (!on) {
    overlaysMod?.unmountMicHint();
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'mic-hint';
    document.body.appendChild(el);
  }
  const txt = MIC_TEXT[app.profile.ui] || MIC_TEXT.en;
  withOverlays((m) => m.renderMicHint(el, txt.asking));
}

// On entering a room: invite the player into the voice call. Pressing the button
// is what makes the browser ask for the microphone, and then they are in.
//
// Not while there is nobody to call, though. A lesson started from a profile
// opens a room with one player in it, and being asked to join a call with
// oneself is a strange way to be handed a vocabulary card. The invitation
// waits for somebody to arrive, and the room asks again then.
let micAsked = false;
async function micGate(): Promise<void> {
  await splashDone;
  if (!app.state || app.modal || voice.on) return;
  if ((app.state.room.players?.length || 1) < 2) return;
  micAsked = true;
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return openModal({ kind: 'mic', reason: 'insecure' });
  const state = await micPermission();
  openModal(state === 'denied' ? { kind: 'mic', reason: 'blocked' } : { kind: 'mic-gate', ready: state === 'granted' });
}

function renderMicGate(m: MicGateModal): void {
  // A connection that drops while the gate is open leaves no room to ask about:
  // there is nothing to draw, and ctx() has nothing to draw it from.
  if (!app.state) return;
  withOverlays((o) => o.renderMicGate(modalRoot, ctx(), m));
}

// ---------- my call panel: full, minimised, or in a floating window ----------

const callRoot = (() => {
  const el = document.createElement('div');
  el.id = 'call-root';
  document.body.appendChild(el);
  return el;
})();

const fmtClock = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function renderCallPanel() {
  // The compact bar shows whenever I'm in the call, except on my own big call view.
  if (!app.state || !voice.on || app.callView === 'full') {
    overlaysMod?.unmountCallPanel();
    return;
  }
  withOverlays((m) => m.renderCallPanel(callRoot, ctx()));
}

setInterval(() => {
  for (const el of document.querySelectorAll<HTMLElement>('.cm-time')) {
    el.textContent = fmtClock(Date.now() - Number(el.dataset.since || Date.now()));
  }
}, 1000);

// ---------- the browser's Back button ----------

// Anything that feels like "a screen further in" (a game or lessons, my big
// call view, a window) leaves one history entry, so Back returns to the room
// instead of leaving the site.
let depth = 0;
function syncHistory() {
  const want = app.state ? (app.state.mode || (voice.on && app.callView === 'full') ? 1 : 0) + (app.modal ? 1 : 0) : 0;
  while (depth < want) {
    history.pushState({ linguino: depth + 1 }, '', location.href);
    depth++;
  }
}

addEventListener('popstate', () => {
  if (depth > 0) depth--;
  if (!app.state) return;
  if (app.modal) {
    if (app.modal.kind === 'mic-gate') return syncHistory();
    closeModal();
    return render();
  }
  if (voice.on && app.callView === 'full') {
    app.callView = 'mini';
    return render();
  }
  if (app.state.mode) {
    if (ctx().isHost) net.send({ t: 'endMode' });
    else toast(t('nav.onlyHost', { name: ctx().name(app.state.room.modeBy || app.state.room.hostId) }));
  }
  render();
});

// ---------- someone is in the call: let the others know ----------

const callers = (): Player[] => (app.state ? app.state.room.players.filter((p) => p.voice && p.connected && p.id !== app.state!.you) : []);
let titleBlink: ReturnType<typeof setInterval> | null = null;
const baseTitle = document.title;

function renderCallInvite(): void {
  let el = document.getElementById('call-invite');
  const list = voice.on ? [] : callers();
  const key = list.map((p) => p.id).join(',');
  const show = list.length && key !== app.callInvite.dismissed;
  // Someone new joined the call: ring once and, in a background tab, flash the title.
  const fresh = list.some((p) => !app.callInvite.seen.includes(p.id));
  app.callInvite.seen = list.map((p) => p.id);
  if (!show) {
    overlaysMod?.unmountCallInvite();
    el?.remove();
    stopBlink();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'call-invite';
    document.body.appendChild(el);
  }
  const names = list.map((p) => p.name).join(', ');
  withOverlays((m) => m.renderCallInvite(el, names));
  if (fresh) {
    ring();
    if (document.hidden) startBlink(`📞 ${names}`);
  }
}

function startBlink(text: string): void {
  stopBlink();
  let on = false;
  titleBlink = setInterval(() => {
    on = !on;
    document.title = on ? text : baseTitle;
  }, 900);
  const stop = () => !document.hidden && stopBlink();
  document.addEventListener('visibilitychange', stop, { once: true });
}

function stopBlink() {
  if (titleBlink) clearInterval(titleBlink);
  titleBlink = null;
  document.title = baseTitle;
}

// A short two-note chime made on the spot (no audio file).
let audioCtx: AudioContext | null = null;
function ring(): void {
  try {
    const ac = (audioCtx ||= new (window.AudioContext || (window as OldWindow).webkitAudioContext)());
    ac.resume?.();
    const now = ac.currentTime;
    [[660, 0], [880, 0.18], [660, 0.6], [880, 0.78]].forEach(([f, at]) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + at);
      g.gain.exponentialRampToValueAtTime(0.18, now + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.16);
      o.connect(g).connect(ac.destination);
      o.start(now + at);
      o.stop(now + at + 0.18);
    });
  } catch {}
}

// ---------- how to play ----------

// A quiet "?" in the card's corner: the rules are one tap away without a fat
// button repeating under every game.
const howButton = (id: string): string => `<button class="how-btn" data-act="tutorial" data-mode="${id}" title="${t('tut.how')}" aria-label="${t('tut.how')}">${icon('circle-help')}</button>`;




// Chat messages carry no language tag. Script settles Russian and Chinese; for
// Latin text, the sender's own languages narrow it down, then telltale words.
function guessLang(text: string, sender: Player | undefined): Lang {
  const has = (l: Lang) => sender?.target === l || sender?.native === l;
  // Kana is only Japanese; Han alone is Chinese unless the sender has Japanese
  // and not Chinese.
  if (/[\u3040-\u30ff]/.test(text)) return 'ja';
  if (/[\u4e00-\u9fff]/.test(text)) return has('ja') && !has('zh') ? 'ja' : 'zh';
  if (/[\u10a0-\u10ff]/.test(text)) return 'ka';
  if (/[\u0400-\u04FF]/.test(text)) {
    // Letters only Kazakh or only Belarusian has; і is in both, so the
    // sender's own languages decide it.
    if (/[әғқңөұүһӘҒҚҢӨҰҮҺ]/.test(text)) return 'kk';
    if (/[ўЎ]/.test(text)) return 'be';
    if (/[іІ]/.test(text)) return has('be') && !has('kk') ? 'be' : has('kk') ? 'kk' : 'be';
    return 'ru';
  }
  const latin = [sender?.target, sender?.native].filter((l): l is Lang => ['en', 'es', 'it', 'de', 'sv', 'no', 'lv', 'fr'].includes(l as string));
  if (latin.length === 1) return latin[0];
  // French before Spanish, which would take its "que": only letters and words
  // Spanish and Italian don't have (not é, è or ù, which they share).
  if (/[çœêâîôûëïÿ«»]|\b(j|qu)['’]|\b[cn]['’]est\b|\b(je|pas|oui|merci|bonjour|avec|nous|vous|mais|pourquoi|suis|aussi|beaucoup|très|où|voilà)(?![\wàâçéèêëîïôûùüÿœ])/i.test(text)) return 'fr';
  if (/[ñ¿¡]|\b(que|los|las|pero|muy|hola|gracias|está|estoy|usted|porque)\b/i.test(text)) return 'es';
  if (/[āēīūģķļņ]|\b(un|ir|nav|paldies|labdien|sveiki|lūdzu|arī|ļoti)\b/i.test(text)) return 'lv';
  // å is in both: Norwegian has æ and ø, Swedish ä and ö (as German does).
  if (/[æø]|\b(ikke|jeg|takk|hei|hva|hvordan|veldig)\b/i.test(text)) return 'no';
  if (/[å]|\b(och|inte|jag|tack|hej|vad|hur|mycket)\b/i.test(text)) return 'sv';
  if (/[äöüß]|\b(und|ich|nicht|ist|das|ein|eine|danke|bitte|auch|aber|sehr)\b/i.test(text)) return 'de';
  if (/[èì]|\b(che|sono|gli|della|perché|ciao|grazie|anche|questo|sei)\b/i.test(text)) return 'it';
  return latin[0] || 'en';
}

// ---------- modals ----------

function openModal(m: AppModal): void {
  app.modal = m;
  renderModal();
}

function closeModal() {
  app.modal = null;
  reactModals(null);
  overlaysMod?.unmountMicGate();
}

// The modals written in React. Like the translator, the bundle is fetched the
// first time one is actually opened, and they all live inside a room.
let modalsMod: ModalsMod | null = null;
let modalsLoading: Promise<void> | null = null;
const modalDeps: ModalDeps = {
  t: (...a) => t(...a),
  get ui() { return app.profile.ui; },
  modeIcon: (id) => MODES[id]?.icon,
  // A game can be started from its tutorial only in the lobby; the vocabulary
  // always opens.
  canStart: (id) => id === 'vocab' || (!!app.state && !app.state.mode && !!MODES[id]),
  inRoom: () => !!app.state,
  others: () => (app.state ? ctx().room.players.filter((p) => p.id !== ctx().you) : []),
  blocked: () => app.state?.room.blocked || [],
  // A server without accounts keeps blocks on the progress id instead.
  signedIn: () => signedIn() || !account.enabled(),
  // Grading a card changes the count in the toolbar, which is not React's.
  rerender: () => render(),
  langName: (l) => LANG_NAMES[app.profile.ui]?.[l as Lang] || l,
  toast: (...a) => toast(...a),
  // What the vocabulary opens on, and in which language.
  get learn() { return app.state ? ctx().me.target : app.profile.learn; },
  get openTopic() { return app.modal?.kind === 'vocab' ? app.modal.topic : null; },
  // How much the exercises may give away.
  get level() { return app.profile.level || 'A1'; },
};

function reactModals(m: AppModal | null): boolean {
  if (modalsMod) return modalsMod.renderModals(m);
  if (!m || modalsLoading) return false;
  modalsLoading = import('/js/modals-react.js').then((mod) => {
    modalsMod = mod;
    mod.mountModals(document.getElementById('modal-react')!, modalDeps);
    mod.renderModals(app.modal);
  }).catch(() => { modalsLoading = null; });
  return true; // it is ours; it is on its way
}

const REACT_MODALS = new Set(['tutorial', 'mic', 'correction', 'deck', 'vocab', 'reading', 'drill', 'story', 'episode', 'player', 'blocks']);

function renderModal(): void {
  const m = app.modal;
  if (!m) return;
  if (REACT_MODALS.has(m.kind)) {
    overlaysMod?.unmountMicGate();
    reactModals(m);
    return;
  }
  reactModals(null);
  if (m.kind === 'mic-gate') return renderMicGate(m);
}

// ---------- events ----------

// The room's own buttons: they only exist while app.state does.
const globalActions = {
  'copy-link': () => {
    const url = `${location.origin}/?room=${app.state!.room.code}`;
    navigator.clipboard?.writeText(url).then(() => toast(t('top.copied'), 'ok'), () => toast(url));
  },
  voice: async () => {
    if (voice.on) {
      voice.disable();
      app.callView = 'mini';
      render();
    } else await joinVoice();
  },
  mute: async () => {
    // Listening only: the mic button is the way to start talking.
    if (voice.listenOnly) {
      voice.disable();
      return joinVoice();
    }
    voice.toggleMute();
    render();
  },
  'mic-retry': () => {
    closeModal();
    joinVoice();
  },
  // Picking the language I am about to speak. When it is not the one I came
  // here to practise, it is simply my own language — which the browser had
  // guessed for me — so the interface and my captions follow it too.
  'gate-lang': (el: HTMLElement) => {
    const lang = el.dataset.lang;
    const me = ctx().me;
    if (me && lang !== me.target && lang !== me.native && oneOf(UI_LANGS, lang)) setSpeak(lang);
    live.actions['live-lang'](el, ctx());
  },
  'gate-join': () => {
    closeModal();
    joinVoice();
  },
  'mic-listen': () => {
    closeModal();
    joinVoice({ listenOnly: true });
  },
  'call-open': async () => {
    app.callView = 'full';
    if (!voice.on) await joinVoice();
    scrollTo({ top: 0, behavior: 'smooth' });
    render();
  },
  screen: async () => {
    if (voice.screen) return voice.stopScreen();
    try {
      await voice.startScreen();
    } catch (e) {
      // Closing the browser's picker is a choice, not a failure.
      const name = (e as { name?: string } | null | undefined)?.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') toast(t('screen.failed'), 'bad');
    }
  },
  'screen-full': (el: HTMLElement) => {
    const video = el.closest('.screen-tile')?.querySelector('video');
    video?.requestFullscreen?.().catch(() => {});
  },
  deaf: () => {
    voice.toggleDeaf();
    render();
  },
  // The call floating above the other tabs, and back from it.
  'float-open': () => float.openFloat(ctx()),
  'float-back': () => float.backToTab(),
  'call-min': () => {
    app.callView = 'mini';
    render();
  },
  'call-hangup': () => {
    if (voice.on) voice.disable();
    app.callView = 'mini';
    render();
  },
  'call-join': () => {
    app.callInvite.shownFor = '';
    joinVoice();
  },
  'call-later': () => {
    app.callInvite.dismissed = callers().map((p) => p.id).join(',');
    renderCallInvite();
  },
  leave: () => {
    if (voice.on) voice.disable();
    live.reset();
    saveSession(null);
    net.close();
    app.state = null;
    app.modeId = null;
    joinedRoom = false;
    micAsked = false;
    app.pendingMode = null;
    feedSeen = null;
    run = null;
    history.replaceState(null, '', '/');
    render();
  },
  'start-mode': (el: HTMLElement) => net.send({ t: 'startMode', mode: el.dataset.mode }),
  'end-mode': () => net.send({ t: 'endMode' }),
  // howButton() always writes data-mode.
  tutorial: (el: HTMLElement) => openModal({ kind: 'tutorial', mode: el.dataset.mode! }),
  'tutorial-go': (el: HTMLElement): unknown => {
    const id = el.dataset.mode;
    closeModal();
    if (id === 'vocab') return globalActions.vocab(el);
    if (id === 'call') return globalActions['call-open']();
    if (app.state && !app.state.mode) net.send({ t: 'startMode', mode: id });
    else render();
  },
  'ui-lang': (el: HTMLElement) => {
    if (setSpeak(el.dataset.lang)) render();
  },
  'team-goal': (el: HTMLElement) => {
    team.setGoal(Number(el.dataset.min));
    render();
  },
  // Installing puts Linguino on the home screen: its own window, its own icon,
  // and the words still there with no connection.
  install: async () => {
    if (await askToInstall()) toast(t('pwa.installed'), 'ok');
    render();
  },
  'to-top': (el: HTMLElement, e: MouseEvent) => {
    e.preventDefault();
    scrollTo({ top: 0, behavior: 'smooth' });
    const form = root.querySelector('form[data-form="home"]');
    const name = form?.querySelector<HTMLInputElement>('input[name="name"]');
    const create = form?.querySelector<HTMLButtonElement>('button[name="create"]');
    // Every button that says "Play now" means it. Whoever has played before
    // already has their name in the form, and used to get nothing but a scroll
    // to a form they had already filled in.
    if (el.tagName === 'BUTTON' && create && name?.value.trim()) return create.click();
    if (name && el.tagName === 'BUTTON') setTimeout(() => name.focus({ preventScroll: true }), 350);
  },
  'reset-scores': () => net.send({ t: 'resetScores' }),
  tab: (el: HTMLElement) => {
    app.sideTab = el.dataset.tab!;
    render();
  },
  // The overflow menu is a popover; browsers without it get a plain toggle.
  more: (el: HTMLElement) => {
    const menu = $(root, '#tb-menu');
    if (!menu || 'popover' in HTMLElement.prototype) return;
    menu.classList.toggle('open');
    el.setAttribute('aria-expanded', String(menu.classList.contains('open')));
  },
  // The translator, opened from the tip beside a game. The panel owns whether
  // it is open; its own button is the way to ask, so this presses it.
  'open-translator': () => {
    renderAI(true);
    // The first time, the panel is still being downloaded: wait for its button.
    const press = (tries: number) => {
      const fab = document.querySelector<HTMLElement>('.ai-fab');
      if (fab) fab.click();
      else if (tries > 0) setTimeout(() => press(tries - 1), 150);
    };
    press(20);
  },
  'sign-in': () => account.signIn(),
  'sign-up': () => account.signUp(),
  'profile-edit': () => {
    app.profileEdit = !app.profileEdit;
    render();
  },
  'profile-edit-cancel': () => {
    app.profileEdit = false;
    render();
  },
  // The room this browser was in, walked back into on purpose. The server still
  // has the seat: the session carries the code and the secret that claims it.
  'resume-room': () => {
    if (!app.session || app.state) return;
    connectWith(null);
    render();
  },
  // And the same room let go of. Somebody looking at "you left a room open"
  // for a game that ended an hour ago needs a way to say so.
  'forget-room': () => {
    if (app.state) return;
    net.close();
    saveSession(null);
    app.joining = false;
    history.replaceState(null, '', '/');
    render();
  },
  // The invitation on the profile, accepted. It is an ordinary join: this
  // browser has never been in that room and has no seat to reclaim.
  'join-invite': () => {
    if (!app.pendingInvite || app.state) return;
    saveSession(null);
    connectWith({ t: 'join', code: app.pendingInvite, ...wire(app.profile) });
    render();
  },
  // Picking a game from the profile. Every game is played in a room, and for
  // the solo ones that room has one player in it; nobody should have to know
  // that, so this opens the room and carries the choice in with it.
  //
  // A game that needs two people does not fail here — it waits. The room opens
  // with the code ready to send, and the game starts by itself the moment
  // somebody arrives (see the 'state' handler).
  'play-mode': (el: HTMLElement) => {
    const id = el.dataset.mode;
    if (!id || !MODES[id]) return;
    if (app.state) return net.send({ t: 'startMode', mode: id });
    app.pendingMode = id;
    saveSession(null);
    connectWith({ t: 'create', ...wire(app.profile) });
    render();
  },
  // A step of the learning path: the activity it is, remembered so that
  // finishing it walks the step (client/path.ts).
  'path-step': (el: HTMLElement) => {
    const { lang, unit, step, ref, solo, min } = el.dataset;
    if (!lang || !unit || !step) return;
    // The lesson alone: its exercises on the whole screen, no room.
    if (step === 'lesson' && solo && ref) {
      path.setTask({ lang, unit, step: 'lesson', ref });
      openModal({ kind: 'drill', step: 'lesson', topics: [], lesson: ref, lang: oneOf(LANGS, lang) ? lang : undefined });
      return;
    }
    if (step === 'lesson') {
      if (!ref) return;
      app.pendingLesson = { lang, id: ref };
      if (app.state) {
        net.send({ t: 'startMode', mode: 'lessons' });
        return;
      }
      app.pendingMode = 'lessons';
      saveSession(null);
      connectWith({ t: 'create', ...wire(app.profile) });
      render();
      return;
    }
    path.setTask({ lang, unit, step: step as path.StepId, ref, min: path.passMark(step as path.StepId, min ? Number(min) : undefined) || undefined });
    // An episode: its story, or its arcade (web/episode).
    if ((step === 'episode' || step === 'arcade') && ref) openModal({ kind: 'episode', id: ref, part: step === 'episode' ? 'story' : 'arcade', lang: oneOf(LANGS, lang) ? lang : undefined });
    else if (step === 'reading') openModal({ kind: 'reading', id: ref });
    else if (step === 'story') openModal({ kind: 'story', id: ref, lang: oneOf(LANGS, lang) ? lang : undefined });
    else if (ref && (step === 'words' || step === 'sentences' || step === 'final')) openModal({ kind: 'drill', step, topics: ref.split(','), lang: oneOf(LANGS, lang) ? lang : undefined });
    else openModal({ kind: 'vocab', topic: ref || null });
  },
  // Practice from the path that walks no step: the words of the units I have
  // already been through, all mixed.
  'path-review': (el: HTMLElement) => {
    const topics = (el.dataset.topics || '').split(',').filter(Boolean);
    const lang = el.dataset.lang;
    if (!topics.length) return;
    path.setTask(null);
    openModal({ kind: 'drill', step: 'review', topics, lang: oneOf(LANGS, lang) ? lang : undefined });
  },
  // The last step, with somebody: a room of my own, and Memory starts on its
  // own the moment a second person arrives. Finishing any game together
  // walks it (the server sees that, not the page).
  'path-together': () => {
    path.setTask(null);
    app.pendingMode = 'memory';
    saveSession(null);
    connectWith({ t: 'create', ...wire(app.profile) });
    toast(t('path.inviteSomeone'), 'ok');
    render();
  },
  // A section of the path finished: the next level, if I say so.
  'path-level': (el: HTMLElement) => {
    const level = el.dataset.level;
    if (!oneOf(LEVELS, level)) return;
    app.profile = { ...app.profile, level };
    store.set('linguino.profile', app.profile);
    if (app.state) net.send({ t: 'profile', level });
    fun.sfx.levelup();
    fun.confetti();
    render();
  },
  'home-edit': () => {
    app.homeEdit = true;
    render();
  },
  'sign-out': () => {
    document.getElementById('acc-menu')?.hidePopover?.();
    account.signOut();
  },
  'acc-delete': async () => {
    document.getElementById('acc-menu')?.hidePopover?.();
    if (!confirm(t('acc.deleteAsk'))) return;
    const err = await account.deleteAccount();
    if (err) return toast(t('acc.deleteFailed'));
    location.href = '/';
  },
  // The backdrop closes it, the sheet itself does not: the same rule the other
  // modals follow.
  'acc-welcome-close': (el: HTMLElement, e: MouseEvent) => {
    if (e.target !== el && el.tagName !== 'BUTTON') return;
    app.welcome = false;
    render();
  },
  theme: () => {
    const next = currentTheme() === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.theme = next;
    store.set('linguino.theme', next);
    applyThemeColor();
    render();
  },
  'correct-open': () => openModal({ kind: 'correction' }),
  // Another player: report (with a reason), block, unblock (client/moderation.ts).
  'player-menu': (el: HTMLElement) => {
    const pid = el.dataset.pid;
    if (pid && app.state && pid !== app.state.you) openModal({ kind: 'player', pid });
  },
  'player-report': (el: HTMLElement) => {
    const { pid, reason } = el.dataset;
    if (!pid || !oneOf(PLAYER_REASONS, reason)) return;
    net.send(reportPlayerMsg(pid, reason));
    closeModal();
  },
  'player-block': (el: HTMLElement) => {
    const pid = el.dataset.pid;
    const name = app.state?.room.players.find((p) => p.id === pid)?.name || '';
    if (!pid) return;
    const ui = app.profile.ui;
    if (!confirm(`${modSay('blockAsk', ui, { name })}\n\n${modSay('blockBody', ui)}`)) return;
    net.send(blockMsg(pid));
    closeModal();
  },
  'player-unblock': (el: HTMLElement) => {
    const pid = el.dataset.pid;
    const name = app.state?.room.players.find((p) => p.id === pid)?.name || '';
    if (!pid) return;
    net.send(unblockMsg(pid));
    toast(modSay('unblocked', app.profile.ui, { name }), 'ok');
    closeModal();
  },
  'blocks-open': () => {
    document.getElementById('acc-menu')?.hidePopover?.();
    openModal({ kind: 'blocks' });
  },
  // Rewrite what I typed in the chat box into the language I'm learning.
  'chat-ai': async (el: HTMLElement) => {
    // The button sits inside the chat form, whose field is named "text".
    const btn = el as HTMLButtonElement;
    const input = (btn.form as HTMLFormElement & { text: HTMLInputElement }).text;
    const text = input.value.trim();
    const me = ctx().me;
    if (!text) return input.focus();
    btn.disabled = true;
    el.classList.add('spin');
    try {
      const out = await aiTranslate(text, me.native, me.target, 'chat');
      if (out && input.value.trim() === text) input.value = out;
    } catch {
      toast(t('ai.failed'), 'bad');
    }
    btn.disabled = false;
    el.classList.remove('spin');
    input.focus();
  },
  'chat-tr': async (el: HTMLElement) => {
    const msg = app.state!.room.chat.find((m) => m.id === el.dataset.id);
    if (!msg) return;
    const me = ctx().me;
    app.chatTr[msg.id] = '';
    render();
    const from = guessLang(msg.text, ctx().player(msg.pid));
    try {
      const out = from === me.native ? msg.text : await aiTranslate(msg.text, from, me.native, `msg:${msg.id}`);
      app.chatTr[msg.id] = out ?? msg.text;
    } catch {
      delete app.chatTr[msg.id];
      toast(t('ai.failed'), 'bad');
    }
    render();
  },
  'corr-accept': (el: HTMLElement) => net.send({ t: 'correctReply', id: el.dataset.id, accept: true }),
  'corr-reject': (el: HTMLElement) => net.send({ t: 'correctReply', id: el.dataset.id, accept: false }),
  'corr-cancel': (el: HTMLElement) => net.send({ t: 'correctReply', id: el.dataset.id, cancel: true }),
  // The backdrop closes only on a click outside the window; a close button always does.
  'modal-close': (el: HTMLElement, e: MouseEvent) => {
    if (e.target === el || el.tagName === 'BUTTON') closeModal();
  },
  deck: () => {
    openModal({ kind: 'deck' });
  },
  support: () => showSupport(),
  sfx: () => {
    fun.toggleSound();
    render();
  },
  'podium-close': () => {
    overlaysMod?.unmountPodium();
    document.getElementById('podium')?.remove();
  },
  vocab: (el: HTMLElement) => {
    openModal({ kind: 'vocab', topic: el.dataset.topic || null });
  },
  // From a cover, straight into that text.
  reading: (el: HTMLElement) => {
    openModal({ kind: 'reading', id: el.dataset.id || undefined });
  },
  // The stories alone: the shelf, or from a cover straight into that story.
  story: (el: HTMLElement) => {
    openModal({ kind: 'story', id: el.dataset.id || undefined });
  },
  'deck-export': () => {
    const blob = new Blob([deckCSV()], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'linguino-deck.csv';
    a.click();
  },
} satisfies Record<string, Action>;
// The same, looked up by the name on the element.
const actionByName: Record<string, Action | undefined> = globalActions;

document.addEventListener('click', onDocClick);
float.setClicks(onDocClick);

function onDocClick(e: MouseEvent) {
  const el = (e.target as Element).closest<HTMLElement>('[data-act]');
  if (!el) return;
  const name = el.dataset.act!;
  const act = actionByName[name];
  if (act) return act(el, e);
  if (live.actions[name] && app.state) return live.actions[name](el, ctx());
  if (talk.actions[name] && app.state) return talk.actions[name](el, ctx());
  const mode = app.state?.mode && MODES[app.state.mode.id];
  if (mode?.onClick) mode.onClick(name, el, ctx(), e);
}

document.addEventListener('submit', (e) => {
  const form = (e.target as Element).closest<HTMLFormElement>('form[data-form]');
  if (!form) return;
  e.preventDefault();
  const kind = form.dataset.form!;
  // No form here has a file input, so every field is a string.
  const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
  if (kind === 'home') {
    const submitter = (e.submitter as HTMLButtonElement | null)?.name;
    app.profile = { ...app.profile, name: data.name.trim(), learn: oneOf(LANGS, data.learn) ? data.learn : app.profile.learn };
    store.set('linguino.profile', app.profile);
    const code = String(data.code || '').trim().toUpperCase();
    const hello = submitter === 'join' ? { t: 'join', code, ...wire(app.profile) } : { t: 'create', ...wire(app.profile) };
    if (submitter === 'join' && code.length !== 4) return toast(t('error.room_not_found'), 'bad');
    saveSession(null);
    connectWith(hello);
    return;
  }
  // What an account cannot answer for somebody: what to call them in a room,
  // what they are learning, and how much of a day they mean to give it. The
  // onboarding asks all three; the edit form on the profile sends the same
  // shape back with only the parts it changed.
  if (kind === 'profile-setup') {
    const name = String(data.name || '').trim();
    if (!name) return toast(t('home.name'), 'bad');
    app.profile = {
      ...app.profile,
      name,
      learn: oneOf(LANGS, data.learn) ? data.learn : app.profile.learn,
      level: oneOf(LEVELS, data.level) ? data.level : app.profile.level,
      uiSet: true,
    };
    store.set('linguino.profile', app.profile);
    // A room already open plays the next card at the new level.
    if (app.state) net.send({ t: 'profile', level: app.profile.level || 'A1' });
    if (data.goal) team.setGoal(Number(data.goal));
    app.profileEdit = false;
    render();
    return;
  }
  // The one door out of the profile that leads to other people.
  if (kind === 'lobby') {
    const submitter = (e.submitter as HTMLButtonElement | null)?.name;
    const code = String(data.code || '').trim().toUpperCase();
    if (submitter === 'join' && code.length !== 4) return toast(t('error.room_not_found'), 'bad');
    saveSession(null);
    connectWith(submitter === 'join' ? { t: 'join', code, ...wire(app.profile) } : { t: 'create', ...wire(app.profile) });
    return;
  }
  if (kind === 'chat') {
    if (data.text.trim()) net.send({ t: 'chat', text: data.text });
    form.reset();
    return;
  }
  if (kind === 'correction') {
    net.send({ t: 'correct', to: data.to, original: data.original, corrected: data.corrected, note: data.note });
    closeModal();
    toast(t('corr.sent'), 'ok');
    return;
  }
  const mode = app.state?.mode && MODES[app.state.mode.id];
  if (mode?.onSubmit) mode.onSubmit(kind, form, data, ctx());
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && app.modal && app.modal.kind !== 'mic-gate') {
    closeModal();
    render();
    return;
  }
  const target = e.target as Element;
  if (e.key !== 'Enter' || e.shiftKey || e.isComposing || !target.matches('textarea')) return;
  const form = target.closest<HTMLFormElement>('form[data-form="ls-answer"], form[data-form="sp-answer"]');
  if (!form) return;
  e.preventDefault();
  form.requestSubmit();
});

document.addEventListener('input', (e) => {
});

document.addEventListener('change', (e) => {
  // An input, or a select: both have the type and value read here.
  const el = (e.target as Element).closest<HTMLInputElement>('[data-change]');
  if (!el) return;
  const kind = el.dataset.change!;
  if (el.type === 'radio') el.closest('.seg')?.querySelectorAll('.seg-opt').forEach((o) => o.classList.toggle('on', o.contains(el)));
  if ((kind === 'home-speak' || kind === 'profile-speak') && setSpeak(el.value)) return render();
  // One of langPicker()'s radios: the home form, the onboarding, the edit form, the lobby.
  if ((kind === 'home-learn' || kind === 'pick-learn') && setLearn(el.value)) return render();
  const mode = app.state?.mode && MODES[app.state.mode.id];
  if (mode?.onChange) mode.onChange(kind, el, ctx());
});

// Countdown ticker for every [data-deadline] on screen; the game's own clock
// ticks through its last five seconds.
setInterval(() => {
  const now = Date.now() + app.offset;
  document.querySelectorAll<HTMLElement & { _tick?: number }>('[data-deadline]').forEach((el) => {
    const left = Number(el.dataset.deadline) - now;
    el.textContent = fmtMs(left);
    el.classList.toggle('low', left < 10000);
    el.classList.toggle('last', left > 0 && left <= 5000);
    const sec = Math.ceil(left / 1000);
    if (sec >= 1 && sec <= 5 && el._tick !== sec && el.closest('#mode-bar, .mem-turn.mine') && !document.hidden) {
      el._tick = sec;
      fun.sfx.tick();
    }
  });
}, 250);

addEventListener('linguino:card-added', () => fun.track('word.saved', { deck: true }));
// The server's count arrived: a mission it finished is news here too.
addEventListener('linguino:me-changed', () => {
  fun.cheer();
  render();
});

// ---------- boot ----------

fun.setupFun({ toast: (...a) => toast(...a), t: (...a) => t(...a), rerender: () => render() });

// The translator panel is React, and React is 70 KB: it is fetched the first
// time somebody is actually in a room, so the home page never pays for it.
let aiPanel: AIMod | null = null;
let aiLoading: Promise<void> | null = null;
const aiDeps: AiDeps = {
  t: (...a) => t(...a),
  me: () => ctx().me,
  langName: (l) => LANG_NAMES[app.profile.ui]?.[l as Lang] || l,
  sendChat: (text) => {
    net.send({ t: 'chat', text });
    app.sideTab = 'chat';
    render();
  },
  // Handed over loosely typed, like Ctx's; they are the deck's own shape.
  addCard: (card) => addCard({ ...card, id: `ai-${Date.now().toString(36)}` }),
  toast: (...a) => toast(...a),
  rerender: () => render(),
};

function renderAI(inRoom: boolean): void {
  if (aiPanel) return aiPanel.renderAI(inRoom);
  if (!inRoom || aiLoading) return;
  aiLoading = import('/js/ai/panel-react.js').then((m) => {
    aiPanel = m;
    m.mountAI(document.getElementById('ai-root')!, aiDeps);
    m.renderAI(true);
  }).catch(() => { aiLoading = null; });
}

// Opening Linguino puts you on your own page, never in a room.
//
// It used to do the opposite: a session from the last twelve hours reconnected
// by itself and an invitation in the address bar walked straight in, so the
// first thing anybody saw was a lobby they had not asked for and could not see
// their own account behind. Both are still one press away — the room waiting
// for you and the invitation are cards on the profile — but the press is now
// the person's.
document.documentElement.lang = app.profile.ui;
app.homeKnown = !!(app.profile.name && app.profile.uiSet);
const profileReady = () => !!(app.profile.name && app.profile.uiSet);
{
  const invite = (new URLSearchParams(location.search).get('room') || '').toUpperCase();
  app.pendingInvite = invite.length === 4 ? invite : '';
  // An invitation to somewhere else replaces the room this browser remembers:
  // there is no sense in offering both, and the link is the fresher intention.
  // An invitation to the room it is already in the middle of is not an
  // invitation at all — the saved seat is the better offer of the two, because
  // it reclaims the place this browser already had rather than taking a new one.
  if (app.session && app.pendingInvite) {
    if (app.pendingInvite === app.session.code) app.pendingInvite = '';
    else saveSession(null);
  }
}

// A seat borrowed from localStorage may be the one another open tab is sitting
// in (the invitation link opened in a second tab, say). Taking it would throw
// that tab out; so ask first, and if a tab answers, this one is somebody new —
// back to the invitation, if that is how it came.
const tabs = typeof BroadcastChannel === 'function' ? new BroadcastChannel('linguino.tabs') : null;
if (tabs) {
  tabs.onmessage = (e: MessageEvent<{ t: string; pid?: string }>) => {
    const { t: kind, pid } = e.data || {};
    if (kind === 'who' && pid && app.session?.pid === pid && !borrowedSession) tabs.postMessage({ t: 'mine', pid });
    if (kind === 'mine' && pid && borrowedSession && app.session?.pid === pid) {
      const code = app.session.code;
      borrowedSession = false;
      app.session = null;
      if (app.joining) {
        net.close();
        app.joining = false;
        app.state = null;
      }
      if (!app.pendingInvite && new URLSearchParams(location.search).get('room')?.toUpperCase() === code) app.pendingInvite = code;
      render();
    }
  };
  if (borrowedSession && app.session) tabs.postMessage({ t: 'who', pid: app.session.pid });
}

// Whether anything is walked into on the way in, decided once.
//
// It has to wait for the server to say whether accounts exist here, which is a
// request: until it comes back every visitor looks like a stranger who may not
// play, and a decision taken then would be "connect to nothing" for everybody.
// So the opening move is held until the answer lands (see account.onChange).
let opened = false;
function openingMove(): void {
  if (opened || app.state) return;
  opened = true;
  // A stranger on a server that asks for an account has nobody to go in as.
  if (locked()) return;
  // And somebody with an account is looking at their profile, where the room
  // that is waiting and the invitation they arrived with are both one press
  // away. Nothing is entered over their head.
  if (signedIn()) return;
  // What is left is a server with no accounts at all, which has no profile to
  // offer anything on: the home page's only button would open a *new* room. So
  // this path keeps what it always did — somebody who reloaded in the middle of
  // a game goes back to the game, because the alternative is losing it.
  if (app.session) return connectWith(null);
  // A name is enough, as it always was: the call asks which language they are
  // about to speak, so the interface language need not be settled first.
  if (app.pendingInvite && app.profile.name) connectWith({ t: 'join', code: app.pendingInvite, ...wire(app.profile) });
}
render();
// Installable, and the words kept for when there is no connection. It starts
// after the first paint on purpose: this must never delay the app opening.
startPWA(() => render());
// Ask the browser to stop treating this site's storage as disposable.
//
// Everything expensive here lives in it: the deck, the progress, and above all
// the translation models, which are a hundred megabytes a pair. Without this
// they are evictable — Android drops them under storage pressure and iOS ages
// them out — and the download somebody already paid for happens again. Asked
// once, after the first paint, and never insisted on: a refusal is an answer.
if (navigator.storage?.persist) {
  navigator.storage.persisted()
    .then((already) => (already ? true : navigator.storage.persist()))
    .catch(() => {});
}
// Progress that follows the person rather than the browser: the deck, the XP,
// the missions, the team's clock and the words already known travel under an
// id this device made for itself.
startSync();
// A newer copy arrived from somewhere else: what is on screen is now behind it.
addEventListener('linguino:synced', () => {
  team.reload();
  // Who I am and what I am learning travel with the account now, so a newer
  // copy can arrive from another device while this page is open.
  const before = app.profile;
  app.profile = loadProfile();
  if (app.profile.ui !== before.ui) {
    t = makeT(app.profile.ui);
    document.documentElement.lang = app.profile.ui;
    void loadStrings(app.profile.ui).then(() => render());
  }
  render();
});
// Whether signing in is even set up here is one question to the server, asked
// after the first paint. The answer decides whether a button exists at all.
account.start();
let accError = '';
account.onChange((now) => {
  // The answer is in: now it can be said whether this visit walks into
  // anything. On a server with accounts it never does.
  if (now.ready) openingMove();
  // A sign-in that fails has to say so: a button that silently does nothing
  // is what people reported as "I cannot sign in".
  if (now.error && now.error !== accError) {
    const blocked = /did not load/.test(now.error);
    toast(`${t(blocked ? 'acc.blocked' : 'acc.failed')} (${now.error})`, 'bad');
  }
  accError = now.error || '';
  // Something actually happened — the account took this device's id, or this
  // device took the account's — and it is said once, plainly: people expect
  // signing in to be the moment they lose what they had.
  if (now.user?.news && !app.welcomeShown) {
    app.welcomeShown = true;
    app.welcome = true;
  }
  // Signing in lands on the profile, like everything else does. The invitation
  // that person arrived with is waiting there as a card; it is not walked into
  // over their head.
  //
  // Signing out is leaving. Staying in a room as somebody who is no longer
  // anybody is the one state this app must not have.
  if (!now.user && now.ready && now.enabled && app.state) {
    globalActions.leave();
    return;
  }
  render();
});
// The opening screen flies its logo into whichever mark the page underneath
// ended up with: the hero's big one, or the small one in the bar that both a
// profile and a room have.
const splashDone = endSplash(() => root.querySelector<HTMLElement>('.hero-logo') || root.querySelector<HTMLElement>('.brand-mark'));
